import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBuilding } from '../../Helpers/BuildingContext';
import { PhotoModel, BlueprintLocation } from '../../types/protocols';
import { currentRoofPlanBlueprint } from '../../Helpers/roofPlan';
import { RelocatableMarker, roofPlanRelocationItems } from '../../Helpers/blueprintRelocation';
import { DeferredBlueprintUpload } from '../../Components/BlueprintUploadModal';
import { RelocationDrawing } from '../../Components/BlueprintRelocationWizard';
import { abortBlueprintRelocation, commitBlueprintRelocation } from '../../api/blueprintRelocationApi';
import { ROOF_PLAN_BLUEPRINT_ENTITY_TYPE } from '../../api/levelsApi';
import { clearRoofDrawing } from '../../api/roofPlanApi';

/** The current roof drawing, with the key its marker positions are stored under. */
export interface RoofDrawing {
    key: string;
    image: PhotoModel;
}

/** A drawing replacement in progress: uploaded but not yet current; the wizard places the markers. */
export interface RoofDrawingRelocation {
    source: RelocationDrawing;
    target: RelocationDrawing;
    items: RelocatableMarker[];
    upload: DeferredBlueprintUpload;
}

interface UseRoofDrawingReturn {
    /** The current roof drawing at `structure/roofPlan`, if one is uploaded. */
    roofDrawing: RoofDrawing | null;
    relocation: RoofDrawingRelocation | null;
    error: string | null;
    clearError: () => void;
    /** The drawing node is written; makes it current, or opens the wizard when markers must move first. */
    handleDeferredUpload: (upload: DeferredBlueprintUpload) => Promise<void>;
    commitRelocation: (placements: Map<string, BlueprintLocation>) => Promise<void>;
    abortRelocation: () => Promise<void>;
    /** "Ta bort ritning": unsets the pointer; the drawing and its positions stay. */
    removeDrawing: () => Promise<void>;
}

/**
 * The roof drawing on the roof page (plan D6/D18): the takplan is derived, never
 * stored, so this hook only owns the drawing at `structure/roofPlan` and the
 * replace/remove flows around it. The page fetches the building; this refetches
 * after each mutation.
 */
export const useRoofDrawing = (buildingKey: string): UseRoofDrawingReturn => {
    const { currentBuilding, fetchBuilding } = useBuilding();
    const [relocation, setRelocation] = useState<RoofDrawingRelocation | null>(null);
    const [error, setError] = useState<string | null>(null);

    const structure = currentBuilding?.model.structure;
    const roofDrawing = useMemo(() => currentRoofPlanBlueprint(structure), [structure]);

    // A stale wizard for another building must not commit into this one.
    useEffect(() => {
        setRelocation(null);
        setError(null);
    }, [buildingKey]);

    const handleDeferredUpload = useCallback(async (upload: DeferredBlueprintUpload) => {
        if (!currentBuilding) return;
        setError(null);
        const items = roofDrawing ? roofPlanRelocationItems(currentBuilding, roofDrawing.key) : [];
        if (!roofDrawing || items.length === 0) {
            // Nothing to move: make the drawing current right away.
            try {
                await commitBlueprintRelocation({ imageKey: upload.key, pointerUpdates: upload.pointerUpdates, placements: [] });
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Ritningen kunde inte bytas');
            }
            await fetchBuilding(buildingKey);
            return;
        }
        setRelocation({
            source: { image: roofDrawing.image, imageKey: roofDrawing.key },
            target: { image: upload.blueprint, imageKey: upload.key },
            items,
            upload,
        });
    }, [buildingKey, currentBuilding, roofDrawing, fetchBuilding]);

    const commitRelocation = useCallback(async (placements: Map<string, BlueprintLocation>) => {
        if (!relocation) return;
        await commitBlueprintRelocation({
            imageKey: relocation.target.imageKey,
            pointerUpdates: relocation.upload.pointerUpdates,
            placements: relocation.items.flatMap(item => {
                const location = placements.get(item.id);
                return location ? [{ dbPath: item.dbPath, location }] : [];
            }),
        });
        setRelocation(null);
        await fetchBuilding(buildingKey);
    }, [relocation, buildingKey, fetchBuilding]);

    const abortRelocation = useCallback(async () => {
        if (!relocation) return;
        await abortBlueprintRelocation({ blueprintPath: relocation.upload.blueprintPath, entityType: ROOF_PLAN_BLUEPRINT_ENTITY_TYPE });
        setRelocation(null);
    }, [relocation]);

    const removeDrawing = useCallback(async () => {
        setError(null);
        try {
            await clearRoofDrawing(buildingKey);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Takritningen kunde inte tas bort');
        }
        await fetchBuilding(buildingKey);
    }, [buildingKey, fetchBuilding]);

    return {
        roofDrawing,
        relocation,
        error,
        clearError: () => setError(null),
        handleDeferredUpload,
        commitRelocation,
        abortRelocation,
        removeDrawing,
    };
};
